// src/layouts/invoices/index.js

import { useEffect, useState } from "react";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import DataTable from "examples/Tables/DataTable";

import { apiFetch } from "api/client";

const TOKEN_KEY = "solvit_auth_token";

function buildDownloadUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const normalized = url.startsWith("/") ? url : `/${url}`;
  if (normalized.startsWith("/solvit-api/")) {
    return `${window.location.origin}${normalized}`;
  }
  return `${window.location.origin}/solvit-api${normalized}`;
}

async function downloadInvoicePdf(invoice, filename = "rechnung.pdf") {
  if (!invoice?.pdfUrl) throw new Error("Für diese Rechnung ist kein PDF hinterlegt.");

  const url = buildDownloadUrl(invoice.pdfUrl);

  const token = localStorage.getItem("solvit_auth_token");
  const res = await fetch(url, {
    method: "GET",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    // same-origin request (kein CORS nötig), aber safe:
    credentials: "include",
  });

  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || `Download fehlgeschlagen (${res.status})`);
  }

  const blob = await res.blob();

  // PDF sanity check (%PDF)
  const headBuf = await blob.slice(0, 8).arrayBuffer();
  const headTxt = new TextDecoder().decode(headBuf);
  if (!headTxt.startsWith("%PDF")) {
    const sampleBuf = await blob.slice(0, 250).arrayBuffer();
    const sampleTxt = new TextDecoder().decode(sampleBuf);
    throw new Error(
      `Kein PDF empfangen. Antwort-Anfang: ${sampleTxt.replace(/\s+/g, " ").slice(0, 180)}`
    );
  }

  const blobUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename.endsWith(".pdf") ? filename : `${filename}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
}

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function loadInvoices() {
      try {
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token) {
          if (mounted) setInvoices([]);
          return;
        }
        const data = await apiFetch("/invoices", { token });
        if (mounted) setInvoices(Array.isArray(data) ? data : data?.invoices || []);
      } catch (err) {
        console.error("Failed to load invoices", err);
        if (mounted) setInvoices([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadInvoices();
    return () => {
      mounted = false;
    };
  }, []);

  const columns = [
    { Header: "Monat", accessor: "month", align: "left" },
    { Header: "Betrag", accessor: "amount", align: "center" },
    { Header: "Status", accessor: "status", align: "center" },
    { Header: "Rechnungsdatum", accessor: "issuedAt", align: "center" },
    { Header: "Download", accessor: "download", align: "center" },
  ];

  const rows = invoices.map((inv) => ({
    month: inv.month || "–",
    amount: inv.amount || "–",
    status: inv.status || "–",
    issuedAt: inv.issuedAt ? new Date(inv.issuedAt).toLocaleDateString("de-DE") : "–",
    download: inv.id ? (
      <MDButton
        variant="outlined"
        color="info"
        size="small"
        onClick={async () => {
          try {
            const niceName = `Rechnung_${inv.month || inv.id}.pdf`;
            await downloadInvoicePdf(inv, niceName);
          } catch (e) {
            console.error(e);
            alert(e?.message || "Download fehlgeschlagen.");
          }
        }}
        disabled={!inv.pdfUrl}
      >
        PDF
      </MDButton>
    ) : (
      "–"
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox mt={1} mb={3}>
        <MDTypography variant="h4" fontWeight="medium">
          Rechnungen
        </MDTypography>
        <MDTypography variant="button" color="text">
          Hier findest du deine Rechnungen als PDF-Download.
        </MDTypography>
      </MDBox>

      {loading ? (
        <MDTypography variant="button">Lade Rechnungen…</MDTypography>
      ) : invoices.length > 0 ? (
        <MDBox>
          <DataTable
            table={{ columns, rows }}
            isSorted={false}
            entriesPerPage={false}
            showTotalEntries={false}
            noEndBorder
          />
        </MDBox>
      ) : (
        <Card>
          <MDBox p={3}>
            <MDBox display="flex" alignItems="center" gap={1} mb={1}>
              <Icon>receipt_long</Icon>
              <MDTypography variant="h6" fontWeight="medium">
                Keine Rechnungen verfügbar
              </MDTypography>
            </MDBox>
            <MDTypography variant="button" color="text">
              Sobald Rechnungen verfügbar sind, erscheinen sie hier automatisch.
            </MDTypography>
          </MDBox>
        </Card>
      )}

      <Footer />
    </DashboardLayout>
  );
}
